/**
 * Add Interview — creates the record, then drops you straight into it.
 *
 * Deliberately asks for the minimum the backend requires (first name, last name, company) plus
 * the recording date, and nothing else. Job title, email, the transcript URL and the transcript
 * itself are all editable on the interview page, which is where the real work happens — so
 * collecting them here just put a form between you and that page.
 *
 * On success it navigates to /interviews/:id instead of showing an alert and leaving you on the
 * list to hunt for the row you just created.
 */
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppContext } from '../../context/AppContext';
import interviewService from '../../services/interviewService';

const BLANK = { first_name: '', last_name: '', company: '', date: '' };

const AddInterviewModal = () => {
  const { state, closeModal } = useAppContext();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState(BLANK);

  const isOpen = state.modals.addInterview;

  const set = (field, value) => setFormData((p) => ({ ...p, [field]: value }));

  const close = () => {
    closeModal('addInterview');
    setFormData(BLANK);
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.first_name.trim() || !formData.last_name.trim() || !formData.company.trim()) {
      setError('First name, last name and company are required.');
      return;
    }

    setLoading(true);
    setError('');
    try {
      const result = await interviewService.createInterview({
        first_name: formData.first_name.trim(),
        last_name: formData.last_name.trim(),
        company: formData.company.trim(),
        date: formData.date ? new Date(formData.date).toISOString() : null,
        priority: 'normal',
        status: 'pending',
      });

      // interviewService returns the whole axios response for this legacy endpoint, and the
      // route replies with a bare Interview (no {success,data} envelope) — so the record can be
      // at .data or at the top level depending on the interceptor path.
      const created = result?.data ?? result;
      const id = created?.id;

      window.dispatchEvent(new CustomEvent('interviews-updated'));
      close();

      if (id) {
        // Straight into the record, ready for the transcript.
        navigate(`/interviews/${id}`);
      }
    } catch (err) {
      // Surface the real reason inline rather than in an alert(). The backend returns a helpful
      // message for the two cases that actually happen: a duplicate interview for the same
      // person on the same date, and a malformed email.
      const detail = err?.response?.data?.detail;
      setError(
        (Array.isArray(detail) ? detail[0]?.msg : detail) ||
        err?.message ||
        'Could not add the interview.'
      );
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const inputCls =
    'mt-1 block w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-kc-blue/30 focus:border-kc-blue';

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen p-4">
        <div className="fixed inset-0 bg-black/50" onClick={close} />

        <div className="relative bg-white rounded-xl shadow-xl w-full max-w-md" data-testid="add-interview-modal">
          <form onSubmit={handleSubmit}>
            <div className="px-6 pt-5 pb-4">
              <h3 className="text-base font-semibold text-gray-900">Add Interview</h3>
              <p className="text-xs text-gray-500 mt-0.5">
                Just enough to create the record — you'll add the transcript on the next screen.
              </p>

              {error && (
                <div className="mt-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700"
                  data-testid="add-interview-error">
                  {error}
                </div>
              )}

              <div className="mt-4 space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">First name *</label>
                    <input type="text" className={inputCls} required autoFocus
                      data-testid="add-interview-first-name"
                      value={formData.first_name}
                      onChange={(e) => set('first_name', e.target.value)} />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Last name *</label>
                    <input type="text" className={inputCls} required
                      data-testid="add-interview-last-name"
                      value={formData.last_name}
                      onChange={(e) => set('last_name', e.target.value)} />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Company *</label>
                  <input type="text" className={inputCls} required
                    data-testid="add-interview-company"
                    value={formData.company}
                    onChange={(e) => set('company', e.target.value)} />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Date of recording</label>
                  <input type="date" className={inputCls}
                    data-testid="add-interview-date"
                    value={formData.date}
                    onChange={(e) => set('date', e.target.value)} />
                  <p className="text-xs text-gray-400 mt-1">
                    Used to tell repeat interviews with the same person apart.
                  </p>
                </div>
              </div>
            </div>

            <div className="px-6 py-3 border-t border-gray-100 flex justify-end gap-2">
              <button type="button" onClick={close} disabled={loading}
                className="px-4 py-2 rounded-lg text-sm font-medium border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50">
                Cancel
              </button>
              <button type="submit" disabled={loading} data-testid="add-interview-submit"
                className="px-4 py-2 rounded-lg text-sm font-medium bg-kc-blue text-white hover:bg-kc-blue-dk disabled:opacity-50">
                {loading ? 'Adding…' : 'Add interview'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AddInterviewModal;
